import { useEffect, useMemo, useState } from "react";
import { Loader2, PackageSearch, UserPlus, Ticket, Zap, MessageSquare, Save } from "lucide-react";
import { toast } from "sonner";
import { fetchOrderContacts, fetchTriggerContacts, sendEmail, fetchDesignTriggers, saveDesignTriggers } from "@/lib/api";

const KINDS = [
  { key: "orders", label: "Orders", icon: PackageSearch, hint: "Everyone whose order is in a status — each order gets its own email with items, totals and tracking filled in." },
  { key: "signups", label: "New sign-ups", icon: UserPlus, hint: "Contacts who created their account in the period." },
  { key: "coupon", label: "Coupon created", icon: Ticket, hint: "Automatic: the moment you create a coupon, this email goes to all your contacts with the new code and link filled in." },
];
const STATUSES = [
  { key: "processing", label: "Processing", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  { key: "pending", label: "Pending", cls: "bg-sky-100 text-sky-800 border-sky-300" },
  { key: "completed", label: "Complete", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
];
const ORDER_DAYS = [[7, "Last 7 days"], [30, "Last 30 days"], [90, "Last 90 days"], [365, "Last year"]];
const SIGNUP_DAYS = [[0, "Today"], [7, "Last 7 days"], [30, "Last 30 days"], [90, "Last 90 days"]];

const chip = (on, cls) => `text-[11px] px-2.5 py-1 rounded-full border font-medium transition-colors ${on ? cls : "bg-white text-zinc-500 border-zinc-200"}`;

export default function DesignTriggersPanel({ userRecordId, template, onTemplateChanged }) {
  const [kind, setKind] = useState("orders");
  const [statuses, setStatuses] = useState(["processing"]);
  const [orderDays, setOrderDays] = useState(90);
  const [signupDays, setSignupDays] = useState(0);
  const [cfg, setCfg] = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);
  const [rows, setRows] = useState([]);
  const [checked, setChecked] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => { setRows([]); setChecked(new Set()); }, [kind]);
  useEffect(() => {
    if (!template?.id || !userRecordId) return;
    fetchDesignTriggers(template.id, userRecordId).then(setCfg).catch(() => setCfg({ coupon_created: false, show_in_chat: true }));
  }, [template?.id, userRecordId]);

  const rowKey = (r) => r.order_record_id || r.id;
  const allChecked = rows.length > 0 && checked.size === rows.length;
  const selected = useMemo(() => rows.filter((r) => checked.has(rowKey(r))), [rows, checked]);

  async function saveCfg(patch) {
    setSavingCfg(true);
    try {
      const r = await saveDesignTriggers(template.id, userRecordId, { ...cfg, ...patch });
      setCfg(r);
      toast.success(r.coupon_created ? "This email will be sent automatically whenever you create a coupon" : "Coupon trigger turned off");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save trigger"); } finally { setSavingCfg(false); }
  }

  async function load() {
    setLoading(true);
    try {
      let list = [];
      if (kind === "orders") {
        if (!statuses.length) { toast.error("Pick at least one order status"); return; }
        list = (await fetchOrderContacts(userRecordId, statuses, orderDays)).orders || [];
      } else {
        list = (await fetchTriggerContacts(userRecordId, "signups", signupDays)).contacts || [];
      }
      setRows(list);
      setChecked(new Set(list.map(rowKey)));
      if (!list.length) toast.message("No contacts found for this trigger");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load contacts");
    } finally {
      setLoading(false);
    }
  }

  async function triggerNow() {
    if (!selected.length) return toast.error("Select at least one recipient");
    const subject = (template.subject || template.title || "").trim();
    if (!subject) return toast.error("This email has no subject — add one in Edit first");
    setSending(true);
    try {
      const res = await sendEmail({
        user_record_id: userRecordId, subject, html: template.html, template_name: template.title || "template",
        recipients: selected.map((r) => ({
          id: r.id, email: r.email, first_name: r.first_name, last_name: r.last_name, full_name: r.full_name, phone: r.phone,
          contact_record_id: r.contact_record_id || r.id, order_record_id: r.order_record_id || null,
        })),
      });
      if (res.sent && !res.failed) toast.success(`Triggered · sent to ${res.sent} ${res.sent === 1 ? "person" : "people"}`);
      else if (res.sent) toast.message(`Sent ${res.sent}, ${res.failed} failed`);
      else toast.error(`All ${res.failed} sends failed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Trigger failed");
    } finally {
      setSending(false);
    }
  }

  const K = KINDS.find((k) => k.key === kind);
  return (
    <div className="space-y-3" data-testid="design-triggers">
      <div className="flex rounded-lg bg-zinc-100 p-0.5" data-testid="trigger-kinds">
        {KINDS.map((k) => (
          <button key={k.key} type="button" onClick={() => setKind(k.key)} className={`flex-1 px-3 h-8 rounded-md text-xs font-medium flex items-center justify-center gap-1 ${kind === k.key ? "bg-white shadow-sm text-zinc-900" : "text-zinc-500"}`} data-testid={`trigger-kind-${k.key}`}>
            <k.icon size={13} /> {k.label}
          </button>
        ))}
      </div>
      <div className="rounded-xl border border-zinc-200 bg-zinc-50/60 p-3 space-y-2">
        <p className="text-[11px] text-zinc-500">{K.hint}</p>
        {kind === "orders" && (
          <div className="flex flex-wrap gap-1.5 items-center" data-testid="trigger-order-statuses">
            {STATUSES.map((s) => <button key={s.key} type="button" onClick={() => setStatuses((a) => (a.includes(s.key) ? a.filter((x) => x !== s.key) : [...a, s.key]))} className={chip(statuses.includes(s.key), s.cls)} data-testid={`trigger-status-${s.key}`}>{s.label}</button>)}
            <select value={orderDays} onChange={(e) => setOrderDays(Number(e.target.value))} className="ml-auto h-7 text-[11px] rounded-md border border-zinc-200 bg-white px-1.5" data-testid="trigger-order-days">{ORDER_DAYS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          </div>
        )}
        {kind === "signups" && (
          <div className="flex flex-wrap gap-1.5" data-testid="trigger-signup-days">
            {SIGNUP_DAYS.map(([v, l]) => <button key={v} type="button" onClick={() => setSignupDays(v)} className={chip(signupDays === v, "bg-violet-100 text-violet-800 border-violet-300")} data-testid={`trigger-signup-days-${v}`}>{l}</button>)}
          </div>
        )}
        {kind === "coupon" && (
          <div className="space-y-2" data-testid="trigger-coupon-config">
            {!template.can_edit ? (
              <p className="text-[11px] text-amber-700">Only the owner of this email can set its automatic triggers.</p>
            ) : !cfg ? <Loader2 size={14} className="animate-spin text-zinc-400" /> : (
              <>
                <label className="flex items-center gap-2 text-xs text-zinc-800 cursor-pointer font-medium">
                  <input type="checkbox" checked={!!cfg.coupon_created} onChange={(e) => setCfg((c) => ({ ...c, coupon_created: e.target.checked }))} className="accent-violet-600" data-testid="trigger-coupon-auto" />
                  <Ticket size={12} className="text-violet-600" /> Send this email automatically when I create a coupon
                </label>
                <label className="flex items-center gap-2 text-xs text-zinc-700 cursor-pointer">
                  <input type="checkbox" checked={!!cfg.show_in_chat} onChange={(e) => setCfg((c) => ({ ...c, show_in_chat: e.target.checked }))} className="accent-violet-600" data-testid="trigger-coupon-chat" />
                  <MessageSquare size={12} className="text-violet-600" /> Also show this email in chat (Molly) once it fires
                </label>
                <p className="text-[11px] text-zinc-500">Recipients: all your contacts · <code>{"{{coupon_code}}"}</code> and <code>{"{{coupon_link}}"}</code> are filled with the new coupon. Up to 50 contacts go out at once, larger lists drip at 10/min.</p>
                {cfg.last_fired_at && <p className="text-[11px] text-emerald-700" data-testid="trigger-coupon-last">Last fired {new Date(cfg.last_fired_at).toLocaleString()} · {cfg.last_fired_count} contacts</p>}
                <button type="button" onClick={() => saveCfg({})} disabled={savingCfg} className="w-full h-9 rounded-lg text-xs font-semibold bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 flex items-center justify-center gap-1.5" data-testid="trigger-coupon-save">
                  {savingCfg ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save trigger
                </button>
              </>
            )}
          </div>
        )}
        {kind !== "coupon" && (
          <button type="button" onClick={load} disabled={loading} className="w-full h-9 rounded-lg text-xs font-semibold bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 flex items-center justify-center gap-1.5" data-testid="trigger-load">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <K.icon size={14} />} Load contacts
          </button>
        )}
      </div>

      {rows.length > 0 && (
        <div className="space-y-2" data-testid="trigger-list">
          <div className="flex items-center justify-between text-[11px] text-zinc-600">
            <span data-testid="trigger-count">{checked.size} of {rows.length} selected</span>
            <button type="button" onClick={() => setChecked(allChecked ? new Set() : new Set(rows.map(rowKey)))} className="text-violet-700 font-medium" data-testid="trigger-select-all">{allChecked ? "Unselect all" : "Select all"}</button>
          </div>
          <div className="max-h-64 overflow-y-auto rounded-lg border border-zinc-200 bg-white divide-y divide-zinc-100">
            {rows.map((r) => {
              const k = rowKey(r);
              return (
                <label key={k} className="flex items-center gap-2 px-2.5 py-1.5 text-xs cursor-pointer hover:bg-zinc-50" data-testid={`trigger-row-${r.order_id || r.id}`}>
                  <input type="checkbox" checked={checked.has(k)} onChange={() => setChecked((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; })} className="accent-violet-600" data-testid={`trigger-check-${r.order_id || r.id}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-zinc-800 truncate">{r.full_name || r.email}{r.order_id ? <span className="text-zinc-400"> · #{r.order_id}</span> : null}</div>
                    <div className="text-[10px] text-zinc-500 truncate">
                      {r.order_id ? `${r.date} · $${Number(r.total || 0).toFixed(2)} · ${r.item_count} item${r.item_count === 1 ? "" : "s"}` : r.added_date ? `Joined ${r.added_date} · ${r.email}` : r.email}
                    </div>
                  </div>
                  {r.status && <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${(STATUSES.find((s) => s.key === r.status) || {}).cls || "bg-zinc-100 text-zinc-600 border-zinc-200"}`}>{r.status}</span>}
                </label>
              );
            })}
          </div>
          <button type="button" onClick={triggerNow} disabled={sending || !selected.length} className="w-full h-10 rounded-lg text-sm font-semibold bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50 flex items-center justify-center gap-1.5" data-testid="trigger-now">
            {sending ? <Loader2 size={15} className="animate-spin" /> : <Zap size={15} />} Trigger now · email {selected.length} {selected.length === 1 ? "person" : "people"}
          </button>
        </div>
      )}
    </div>
  );
}
